import { Component } from '@theme/component';

/**
 * Challenge picker: totals, enable Review, dedupe cart lines, add pledge.
 *
 * @typedef {Object} PbRefs
 * @property {HTMLElement} [total]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [status]
 */

/**
 * @extends {Component<PbRefs>}
 */
class PbChallengePicker extends Component {
  requiredRefs = ['total', 'submit', 'error', 'status'];

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onChange, { signal });
    this.refs.submit?.addEventListener('click', this.#onSubmit, { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    super.disconnectedCallback();
  }

  #onChange = () => {
    this.#sync();
  };

  #sync() {
    const selected = this.#selectedItems();
    const totalEl = this.refs.total;
    const submit = this.refs.submit;
    const currency = this.dataset.currency || 'NZD';
    const locale = this.dataset.locale || undefined;

    if (totalEl) {
      if (selected.length === 0) {
        totalEl.textContent = this.dataset.emptyTotal || 'Pick a challenge';
      } else {
        const cents = selected.reduce((sum, item) => sum + item.price, 0);
        // Narrow symbol before amount, ISO code after — e.g. "$300 NZD"
        const amount = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(cents / 100);
        totalEl.textContent = `${amount} ${currency}`;
      }
    }

    const ready =
      selected.length > 0 &&
      this.#toggledChallenges().every((challenge) => {
        return selected.some((item) => item.challengeId === challenge);
      });

    if (submit) {
      submit.disabled = !ready;
    }

    this.#clearError();
  }

  /** @returns {string[]} */
  #toggledChallenges() {
    return Array.from(this.querySelectorAll('[data-pb-challenge-toggle]:checked')).map((input) => {
      const host = /** @type {HTMLElement} */ (input.closest('[data-pb-challenge]'));
      return host?.dataset.pbChallenge || '';
    });
  }

  #selectedItems() {
    /** @type {{ id: number, price: number, challengeId: string, rateNzd: string, rateGbp: string }[]} */
    const items = [];

    for (const challenge of this.querySelectorAll('[data-pb-challenge]')) {
      const toggle = challenge.querySelector('[data-pb-challenge-toggle]');
      const amounts = challenge.querySelector('[data-pb-amounts]');
      if (!(toggle instanceof HTMLInputElement) || !toggle.checked) {
        if (amounts instanceof HTMLElement) amounts.hidden = true;
        continue;
      }
      if (amounts instanceof HTMLElement) amounts.hidden = false;

      const chosen = challenge.querySelector('.pb-amount__input:checked');
      if (!(chosen instanceof HTMLInputElement)) continue;

      const rateNzd = chosen.dataset.pbRateNzd || chosen.dataset.pbRate || '';
      const rateGbp = chosen.dataset.pbRateGbp || rateNzd;

      items.push({
        id: Number(chosen.dataset.pbVariantId || chosen.value),
        price: Number(chosen.dataset.pbPrice || 0),
        challengeId: challenge.getAttribute('data-pb-challenge') || '',
        rateNzd,
        rateGbp,
      });
    }

    return items;
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    const submit = this.refs.submit;
    if (!submit || submit.disabled) return;

    const items = this.#selectedItems();
    if (items.length === 0) return;

    const handle = this.dataset.probroHandle || '';
    const name = this.dataset.probroName || '';
    const gender = this.dataset.probroGender || '';
    const root = window.Shopify?.routes?.root || '/';
    const cartUrl = this.dataset.cartUrl || `${root}cart`;
    const cartJsUrl = this.dataset.cartJsUrl || `${root}cart.js`;
    const cartUpdateUrl = this.dataset.cartUpdateUrl || `${root}cart/update.js`;
    const cartAddUrl = this.dataset.cartAddUrl || `${root}cart/add.js`;

    submit.disabled = true;
    this.#clearError();

    try {
      const cartRes = await fetch(cartJsUrl, { headers: { Accept: 'application/json' } });
      if (!cartRes.ok) throw new Error('Could not load cart');
      const cart = await cartRes.json();

      /** @type {Record<string, number>} */
      const updates = {};
      for (const line of cart.items || []) {
        const lineHandle = line.properties?._probro_handle;
        if (lineHandle && lineHandle === handle && line.key) {
          updates[line.key] = 0;
        }
      }

      if (Object.keys(updates).length > 0) {
        const updateRes = await fetch(cartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!updateRes.ok) throw new Error('Could not update cart');
      }

      const currency = (this.dataset.currency || 'NZD').toUpperCase();
      const addRes = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => {
            const rate = currency === 'GBP' ? item.rateGbp : item.rateNzd;
            /** @type {Record<string, string>} */
            const properties = {
              ProBro: name,
              _probro_handle: handle,
            };
            if (gender) properties._gender = gender;
            if (item.rateNzd) properties._rate_nzd = item.rateNzd;
            if (item.rateGbp) properties._rate_gbp = item.rateGbp;
            if (rate) properties.Challenge = rate;
            return {
              id: item.id,
              quantity: 1,
              properties,
            };
          }),
        }),
      });

      if (!addRes.ok) {
        const errBody = await addRes.json().catch(() => ({}));
        throw new Error(errBody.description || errBody.message || 'Could not add pledge');
      }

      window.location.href = cartUrl;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong';
      this.#showError(message);
      submit.disabled = false;
      this.#sync();
    }
  };

  /** @param {string} message */
  #showError(message) {
    const error = this.refs.error;
    const status = this.refs.status;
    if (error) {
      error.hidden = false;
      error.textContent = message;
    }
    if (status) status.textContent = message;
  }

  #clearError() {
    const error = this.refs.error;
    const status = this.refs.status;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    if (status) status.textContent = '';
  }
}

/**
 * Keeps visible Challenge line property aligned with presentment currency for checkout.
 * Uses hidden _rate_nzd / _rate_gbp set at add-to-cart. Migrates legacy Rate keys.
 *
 * Property updates change Shopify line keys, so remove links are disabled while syncing
 * and the page reloads once afterwards so line indexes / markup stay in sync.
 */
class PbPledgeRateSync extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    this.#syncRates();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {boolean} locked */
  #setCartControlsLocked(locked) {
    const checkoutButtons = document.querySelectorAll('[name="checkout"]');
    for (const button of checkoutButtons) {
      if (button instanceof HTMLButtonElement || button instanceof HTMLInputElement) {
        button.disabled = locked;
      }
    }

    for (const link of document.querySelectorAll('a.pb-pledge__remove')) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (locked) {
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      } else {
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      }
    }
  }

  async #syncRates() {
    const currency = (this.dataset.currency || 'NZD').toUpperCase();
    const rateKey = currency === 'GBP' ? '_rate_gbp' : '_rate_nzd';
    const root = window.Shopify?.routes?.root || '/';
    const cartJsUrl = this.dataset.cartJsUrl || `${root}cart.js`;
    const cartChangeUrl = this.dataset.cartChangeUrl || `${root}cart/change.js`;
    let didUpdate = false;

    this.#setCartControlsLocked(true);

    try {
      const cartRes = await fetch(cartJsUrl, {
        headers: { Accept: 'application/json' },
        signal: this.#abort?.signal,
      });
      if (!cartRes.ok) return;

      const cart = await cartRes.json();
      for (const line of cart.items || []) {
        const props = line.properties || {};
        if (!props._probro_handle) continue;

        const desired = props[rateKey];
        if (!desired) continue;

        const needsUpdate = props.Challenge !== desired || Object.hasOwn(props, 'Rate');
        if (!needsUpdate) continue;

        const nextProperties = { ...props, Challenge: desired };
        delete nextProperties.Rate;
        const changeRes = await fetch(cartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          signal: this.#abort?.signal,
          body: JSON.stringify({
            id: line.key,
            quantity: line.quantity,
            properties: nextProperties,
          }),
        });
        if (!changeRes.ok) throw new Error('Could not sync challenge labels');
        didUpdate = true;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('[pb-pledge-rate-sync]', error);
    } finally {
      // Keys change when Challenge is rewritten; reload so remove links stay valid.
      if (didUpdate) {
        window.location.reload();
      } else {
        this.#setCartControlsLocked(false);
      }
    }
  }
}

if (!customElements.get('pb-challenge-picker')) {
  customElements.define('pb-challenge-picker', PbChallengePicker);
}

if (!customElements.get('pb-pledge-rate-sync')) {
  customElements.define('pb-pledge-rate-sync', PbPledgeRateSync);
}

document.addEventListener('shopify:section:load', (event) => {
  const section = /** @type {CustomEvent} */ (event).target;
  if (!(section instanceof Element)) return;
  if (section.querySelector('pb-challenge-picker') && !customElements.get('pb-challenge-picker')) {
    customElements.define('pb-challenge-picker', PbChallengePicker);
  }
  if (section.querySelector('pb-pledge-rate-sync') && !customElements.get('pb-pledge-rate-sync')) {
    customElements.define('pb-pledge-rate-sync', PbPledgeRateSync);
  }
});
